import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { brandService, type BrandIdentity } from '@/services/brandService'
import { CustomDomainService } from '@/services/customDomainService'
import { useCurrentHost } from '@/composables/useCurrentHost'
import { brandPackService, type ActiveBrandPack } from '@/services/brandPackService'

/**
 * Holds the resolved brand identity for the current hostname.
 *
 * Bootstrapped once at app startup from main.ts. When the host is the default
 * Zaveato domain (or any unmapped host), brand stays null and the SPA renders
 * the default Zaveato chrome unchanged — that's the isolation guarantee.
 *
 * `isWhiteLabel` is the only signal components should gate visual changes on.
 * Subdomains (mybusiness.zaveato.co.uk) still load the brand for personalisation
 * but isWhiteLabel stays false so the Zaveato shell remains visible.
 */
/**
 * Resolution outcome for the current hostname. Consumers (router guard,
 * NotFoundView) read this to decide 404 vs graceful degradation.
 *
 *   - `idle`         — bootstrap() not yet called
 *   - `loading`      — API call in flight
 *   - `resolved`     — shop identified; safe to render WL chrome
 *   - `unknown-host` — backend returned 404 for this hostname; NOT ours →
 *                      router safely redirects to /404
 *   - `error`        — transient failure (network / 5xx / timeout). Router
 *                      MUST NOT 404 — the raw HTML already has correct WL
 *                      branding and a retry is scheduled. This is the state
 *                      that used to collapse every URL into /404 in Google.
 */
export type BrandResolutionState =
  | 'idle'
  | 'loading'
  | 'resolved'
  | 'unknown-host'
  | 'error'

export const useBrandStore = defineStore('brand', () => {
  const brand = ref<BrandIdentity | null>(null)
  const isLoaded = ref(false)
  const resolutionState = ref<BrandResolutionState>('idle')
  const resolutionAttempts = ref(0)

  /**
   * Active WL brand pack for this shop (Phase 2).
   *
   * Fetched right after `brand` resolves for WL hosts. Holds:
   *   - `engineId` — which engine the pack picked (e.g. "board-engine")
   *   - `manifest` — tokens + content the engine renders
   *   - `assetsBaseUrl` — where the pack's uploaded assets live (S3 signed URL)
   *
   * Null when the shop has NO pack uploaded — falls back to the `default`
   * engine + the legacy Shop-field bridge in `useWlTokens`.
   */
  const activePack = ref<ActiveBrandPack | null>(null)

  const isWhiteLabel = computed(() => brand.value?.isWhiteLabel === true)
  const hasBrand = computed(() => brand.value !== null)
  const activeEngineId = computed(() => activePack.value?.engineId ?? 'default')

  /**
   * Key for stashing preview-mode shopId in sessionStorage. Preview mode survives
   * any hard navigation in the tab (Stripe redirect, page reload, etc.) so the
   * shop's branding doesn't get dropped mid-checkout when external services
   * round-trip the user back to localhost with no query string.
   */
  const PREVIEW_STORAGE_KEY = 'wl-preview-shopId'

  async function bootstrap(): Promise<void> {
    if (isLoaded.value) return
    resolutionState.value = 'loading'
    resolutionAttempts.value += 1

    // Preview Mode: `?preview=wl&shopId=<guid>` lets a logged-in provider preview
    // their own storefront in white-label without hosts files or DNS. Auth-gated
    // — the underlying GET /branding/{shopId} endpoint enforces ownership.
    //
    // We also honour the same shopId from sessionStorage so the preview survives:
    //   - Stripe's hard-redirect return_url after payment
    //   - manual page reloads
    //   - any other navigation that drops the query string
    // The session storage is intentionally tab-scoped — a fresh tab without
    // preview= in the URL will default to Zaveato, which is the correct behaviour.
    const params = new URLSearchParams(window.location.search)
    let previewShopId: string | null = null

    if (params.get('preview') === 'wl' && params.get('shopId')) {
      previewShopId = params.get('shopId')
      try { sessionStorage.setItem(PREVIEW_STORAGE_KEY, previewShopId!) } catch {}
    } else {
      try { previewShopId = sessionStorage.getItem(PREVIEW_STORAGE_KEY) } catch {}
    }

    if (previewShopId) {
      try {
        const b = await CustomDomainService.getBranding(previewShopId)
        brand.value = { ...b, isWhiteLabel: true }
        // Preview may target a specific pack version via ?wl-pack-preview=<id>
        const previewPackId = params.get('wl-pack-preview')
        await loadActivePack(b.shopId, previewPackId)
        resolutionState.value = 'resolved'
      } catch {
        // Auth failed or not the owner — drop the stale preview token so we
        // don't keep retrying on every reload, then fall through silently.
        try { sessionStorage.removeItem(PREVIEW_STORAGE_KEY) } catch {}
        resolutionState.value = 'error'
      }
      isLoaded.value = true
      return
    }

    // Skip the lookup entirely on canonical Zaveato hosts — saves a request
    // on the dominant path. Custom domains and subdomains both fall through.
    // Canonical/WL classification lives in the single-source-of-truth
    // composable `useCurrentHost` (backed by config/hosts.ts). Adding a new
    // regional host = one edit in config/hosts.ts.
    const { hostname, isCanonical } = useCurrentHost()

    if (isCanonical.value) {
      // Canonical Zaveato — no shop to resolve. Not "unknown-host" (that's
      // an API-level distinction); a resolved-null on canonical means "no
      // WL shop, render marketplace chrome" which is the correct state.
      resolutionState.value = 'resolved'
      isLoaded.value = true
      return
    }

    try {
      brand.value = await brandService.getBrandForHost(hostname.value)
      if (brand.value === null) {
        // Backend returned 404 for this hostname — genuinely unknown. Router
        // can safely redirect to /404 for this state.
        resolutionState.value = 'unknown-host'
      } else {
        // Fetch the active pack for WL shops. Non-WL hosts get null.
        if (brand.value.isWhiteLabel && brand.value.shopId) {
          await loadActivePack(brand.value.shopId, null)
        }
        resolutionState.value = 'resolved'
      }
    } catch (err) {
      // Transient — network, 5xx, timeout, CORS. Router MUST NOT 404 on
      // this: falling back to /404 for every WL URL on every network hiccup
      // is what caused the SPA-wide SEO collapse. Instead we stay in
      // 'error', let the SPA render the requested route (raw HTML has
      // correct WL meta), and reschedule.
      console.warn('brand store: transient resolution error; will retry', err)
      resolutionState.value = 'error'
      scheduleRetry()
    } finally {
      isLoaded.value = true
    }
  }

  /**
   * Retry after a transient failure — one-shot with 3s backoff. Prevents a
   * tight retry loop against a struggling API; if it still fails after one
   * retry we stop and leave the SPA to degrade gracefully.
   */
  function scheduleRetry() {
    if (resolutionAttempts.value >= 2) return
    setTimeout(() => {
      isLoaded.value = false
      bootstrap().catch(() => { /* swallowed — status is already 'error' */ })
    }, 3000)
  }

  /**
   * Load the shop's active pack (or a specific version in preview mode).
   * Swallowed on error — a missing/broken pack falls back to `default` engine
   * so the shop's site still renders.
   */
  async function loadActivePack(shopId: string, previewPackId: string | null) {
    try {
      activePack.value = await brandPackService.getActive(shopId, previewPackId ?? undefined)
    } catch (err) {
      console.warn('brand store: failed to load active pack — falling back to default engine', err)
      activePack.value = null
    }
  }

  return {
    brand,
    activePack,
    isLoaded,
    isWhiteLabel,
    hasBrand,
    activeEngineId,
    resolutionState,
    bootstrap,
  }
})
